import React from "react";
import Hero from "../sections/Hero";
import Marquee from "../sections/Marquee";
import Services from "../sections/Services";
import PortfolioTeaser from "../sections/PortfolioTeaser";
import About from "../sections/About";
import Testimonials from "../sections/Testimonials";
import CtaBand from "../sections/CtaBand";
import SEO from "../components/SEO";
import { useI18n } from "../i18n/I18nProvider";
import { getSiteOrigin } from "../siteOrigin";

export default function Home() {
  const { lang } = useI18n();
  const SITE = getSiteOrigin();
  const heroImg = `${SITE}/generated/founder_cinema_camera.jpg`;
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": ["ProfessionalService", "LocalBusiness"],
    "@id": `${SITE}/#studio`,
    name: "Phaneroo Prod",
    alternateName: ["Phaneroo Ventures", "Phaneroo Productions"],
    description: lang === "fr"
      ? "Studio bilingue de production vidéo, photographie et contenu cinématographique à Moncton, NB. Films de marque, portraits éditoriaux, aériens et campagnes sociales partout au Canada Atlantique."
      : "Bilingual studio for cinematic video, photography and content production based in Moncton, NB. Brand films, editorial portraits, aerial work and social campaigns across Atlantic Canada.",
    image: heroImg,
    logo: heroImg,
    url: `${SITE}/`,
    telephone: "+1-506-871-6122",
    email: "contact@phanerooventures.com",
    priceRange: "$$",
    currenciesAccepted: "CAD",
    paymentAccepted: ["Cash", "Credit Card", "Bank Transfer", "Stripe"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Downtown Moncton",
      addressLocality: "Moncton",
      addressRegion: "NB",
      postalCode: "E1C 1A1",
      addressCountry: "CA",
    },
    geo: { "@type": "GeoCoordinates", latitude: 46.0878, longitude: -64.7782 },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00", closes: "18:00",
    }],
    areaServed: [
      { "@type": "Country", name: "Canada" },
      { "@type": "AdministrativeArea", name: "New Brunswick" },
      { "@type": "AdministrativeArea", name: "Nova Scotia" },
      { "@type": "AdministrativeArea", name: "Prince Edward Island" },
      { "@type": "AdministrativeArea", name: "Quebec" },
    ],
    serviceType: ["Video Production", "Photography", "Drone / Aerial", "Social Content", "Art Direction"],
    knowsLanguage: ["fr-CA", "en-CA"],
    sameAs: ["https://instagram.com/phanerooprod", "https://youtube.com/@phanerooprod"],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "12",
    },
  };
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    name: "Phaneroo Prod",
    url: `${SITE}/`,
    inLanguage: ["fr-CA", "en-CA"],
    publisher: { "@id": `${SITE}/#studio` },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE}/portfolio?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: lang === "fr"
          ? "Combien coûte une production vidéo à Moncton, NB ?"
          : "How much does video production cost in Moncton, NB?",
        acceptedAnswer: {
          "@type": "Answer",
          text: lang === "fr"
            ? "Les tarifs de production vidéo à Moncton varient de 1 800 $ CAD pour une capsule courte (forfait Essentiel) à 4 500 $ CAD pour un film de marque complet (forfait Signature). Les productions Cinema sur mesure sont disponibles sur devis. Tous les prix incluent la cession complète des droits commerciaux. HST 15 % en sus en N.-B."
            : "Video production costs in Moncton, NB range from CAD $1,800 for a short deliverable (Essential package) to CAD $4,500 for a full brand film (Signature package). Custom Cinema productions are available on quote. All prices include full commercial rights transfer. 15% HST extra in NB.",
        },
      },
      {
        "@type": "Question",
        name: lang === "fr"
          ? "Phaneroo Prod offre-t-il la production bilingue en français et en anglais ?"
          : "Does Phaneroo Prod offer bilingual video production in French and English?",
        acceptedAnswer: {
          "@type": "Answer",
          text: lang === "fr"
            ? "Oui. Phaneroo Prod est le studio de production cinématique bilingue de Moncton, produisant nativement en français et en anglais — pas uniquement par traduction. Tous les projets peuvent être livrés avec versions FR et EN complètes."
            : "Yes. Phaneroo Prod is Moncton's bilingual cinematic production studio, producing natively in both French and English — not just translation. All projects can be delivered with complete FR and EN versions.",
        },
      },
      {
        "@type": "Question",
        name: lang === "fr"
          ? "Quelles régions Phaneroo Prod dessert-il ?"
          : "What areas does Phaneroo Prod serve?",
        acceptedAnswer: {
          "@type": "Answer",
          text: lang === "fr"
            ? "Phaneroo Prod est basé à Moncton, N.-B., et dessert tout le Canada Atlantique : Fredericton, Saint John, Halifax, Charlottetown, Shediac, ainsi que partout au Canada sur demande."
            : "Phaneroo Prod is based in Moncton, NB, and serves all of Atlantic Canada including Fredericton, Saint John, Halifax, Charlottetown, Shediac, and nationally across Canada on request.",
        },
      },
      {
        "@type": "Question",
        name: lang === "fr"
          ? "Phaneroo Prod est-il certifié pour les prises de vue aériennes par drone ?"
          : "Is Phaneroo Prod certified for drone aerial filming?",
        acceptedAnswer: {
          "@type": "Answer",
          text: lang === "fr"
            ? "Oui. Phaneroo Prod est opéré par un pilote RPAS certifié niveau avancé de Transports Canada. Toutes les prises de vue aériennes au-dessus du Nouveau-Brunswick et du Canada Atlantique comprennent assurance responsabilité commerciale, plans de vol et autorisations."
            : "Yes. Phaneroo Prod is operated by a Transport Canada Advanced-certified RPAS pilot. All drone aerial work across New Brunswick and Atlantic Canada includes commercial liability insurance, flight plans and permits.",
        },
      },
    ],
  };
  const schema = [businessSchema, websiteSchema, faqSchema];
  return (
    <main data-testid="page-home">
      <SEO
        title={lang === "fr"
          ? "Production Vidéo & Photo Cinématique Moncton NB — Bilingue FR/EN"
          : "Cinematic Video & Photo Production Studio Moncton NB — Bilingual FR/EN"}
        description={lang === "fr"
          ? "Phaneroo Prod — studio de production vidéo, photographie et contenu cinématographique à Moncton, NB. Bilingue FR/EN, projets partout au Canada Atlantique. Films de marque, drone RPAS certifié, photographie éditoriale."
          : "Phaneroo Prod — cinematic video, photography and content production studio in Moncton, NB. Bilingual FR/EN, projects across Atlantic Canada. Brand films, Transport Canada certified drone, editorial photography."}
        path="/"
        lang={lang}
        schema={schema}
      />
      <Hero />
      <Marquee />
      <Services />
      <PortfolioTeaser />
      <About />
      <Testimonials />
      <CtaBand />
    </main>
  );
}
